import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Calendar, PlayCircle } from 'lucide-react';

export type InlineEvent = {
  id: string;
  title: string;
  description: string | null;
  event_date: string; // ISO
  event_end_date: string | null; // ISO
  thumbnail_url: string | null;
  event_url: string | null;
  is_started: boolean | null;
  is_ended: boolean | null;
  replay_available: boolean | null;
};

export function LiveEventInlineCard({ event }: { event: InlineEvent }) {
  const start = new Date(event.event_date);
  const stateLive = !!event.is_started && !event.is_ended;
  const stateEnded = !!event.is_ended;

  // Compact countdown (for scheduled events)
  const Countdown = ({ target }: { target: Date }) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
      const t = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(t);
    }, []);
    const diff = Math.max(0, target.getTime() - now);
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const Box = ({ label, value }: { label: string; value: number }) => (
      <div className="flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-md px-2 py-1 min-w-[42px]">
        <div className="text-[#c9fffc] text-sm font-bold tracking-wider">{String(value).padStart(2, '0')}</div>
        <div className="text-[9px] uppercase text-gray-400">{label}</div>
      </div>
    );

    return (
      <div className="flex items-center gap-1.5">
        <Box label="Days" value={days} />
        <span className="text-gray-500">:</span>
        <Box label="Hours" value={hours} />
        <span className="text-gray-500">:</span>
        <Box label="Mins" value={minutes} />
        <span className="text-gray-500">:</span>
        <Box label="Secs" value={seconds} />
      </div>
    );
  };

  const googleUrl = (() => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const toG = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
    const end = event.event_end_date ? new Date(event.event_end_date) : new Date(start.getTime() + 60 * 60 * 1000);
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title || 'Live Event',
      dates: `${toG(start)}/${toG(end)}`,
      details: event.description ? `${event.description}\n\nLink: ${event.event_url || ''}` : `Link: ${event.event_url || ''}`,
      location: 'Online',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  })();

  return (
    <div className="mt-4 bg-[#0d1117]/70 border border-white/10 rounded-xl overflow-hidden">
      <div className="flex gap-3 p-3">
        <div className="w-40 flex-shrink-0">
          <div className="aspect-[16/9] rounded-lg overflow-hidden bg-gray-700">
            {event.thumbnail_url ? (
              <img src={event.thumbnail_url} alt={event.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400"><Calendar className="w-6 h-6"/></div>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs mb-1">
            <span className="text-amber-300 font-semibold">Event</span>
            {stateLive ? (
              <span className="text-red-300">Live now</span>
            ) : stateEnded ? (
              <span className="text-gray-400">Ended</span>
            ) : (
              <span className="text-gray-300">Scheduled</span>
            )}
          </div>
          <h4 className="text-white font-semibold truncate">{event.title}</h4>
          <div className="text-sm text-gray-300 flex items-center gap-2">
            <Calendar className="w-4 h-4"/>
            <span>{format(start, 'EEE, MMM d')}</span>
            <span className="text-gray-400">{`${format(start, 'h:mm a')} - ${format(event.event_end_date ? new Date(event.event_end_date) : new Date(start.getTime() + 60 * 60 * 1000), 'h:mm a')}`}</span>
          </div>
          {event.description && (
            <p className="text-gray-300 text-sm line-clamp-2 mt-1">{event.description}</p>
          )}

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {stateLive && event.event_url ? (
              <a
                href={event.event_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#c9fffc] text-gray-900 text-sm font-semibold hover:bg-[#a0fcf9]"
              >
                <PlayCircle className="w-4 h-4"/> Join Now
              </a>
            ) : !stateEnded ? (
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10"
              >
                <Calendar className="w-4 h-4"/> Add to Calendar
              </a>
            ) : null}
            {!stateLive && !stateEnded && (
              <div className="ml-auto">
                <Countdown target={start} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
